/**
 * Drive Management Modal (UI-only)
 * - Lists current System Roles with access to a drive (org_drive, user_files, my_files, media_gallery)
 * - Provides search for system roles (no save for now)
 */

import { SpinnerController } from '../../../../public/assets/ts/spinner_controller';
declare var vmDriveManagement: {
    ajaxUrl: string;
    nonce: string;
    strings: {
        title: string;
        systemRolesWithAccess: string;
        addSystemRole: string;
        noRolesConfigured: string;
        loading: string;
        done: string;
        searching: string;
        noResults: string;
    };
};

(function($: any) {
    'use strict';

    class DriveManagementModal {
        private isOpen: boolean = false;
        private $root: JQuery | null = null;
        private currentDrive: 'media_gallery' | 'user_files' | 'my_files' | 'org_drive' = 'org_drive';
        private selectedRoles: { [roleId: string]: { id: string; name: string; access_type: 'viewer' | 'contributor' | 'editor'; member_count?: number } } = {};
		private spinnerController: SpinnerController | null = null;

        constructor() {
			this.spinnerController = new SpinnerController();
            this.bindEvents();
        }

        private bindEvents(): void {
            $(document).on('click', '#vm-drive-management-modal .custom-wc-modal-close, #vm-drive-management-modal .wc-backbone-modal-backdrop', () => {
                this.close();
            });

            $(document).on('input', '#vm-drive-management-modal .vm-user-search-input', (e: any) => {
                const term = $(e.currentTarget).val()?.toString().trim() || '';
                if (term.length >= 2) {
                    this.searchRoles(term);
                } else {
                    ($('#vm-drive-management-modal .vm-search-results') as any).hide().empty();
                }
            });

            // Change access type (reuse vm-role-select class for consistent styling)
            $(document).on('change', '#vm-drive-management-modal .vm-drive-mgmt-access-select', (e: any) => {
                const roleId = $(e.currentTarget).data('user-id');
                const value = $(e.currentTarget).val();
                if (roleId && (value === 'viewer' || value === 'contributor' || value === 'editor')) {
                    if (this.selectedRoles[roleId]) {
                        this.selectedRoles[roleId].access_type = value;
                    }
                }
            });

            // Remove role (match existing class used elsewhere)
            $(document).on('click', '#vm-drive-management-modal .vm-remove-user', (e: any) => {
                const roleId = $(e.currentTarget).data('user-id');
                if (roleId && this.selectedRoles[roleId]) {
                    delete this.selectedRoles[roleId];
                    this.renderRolesList();
                }
            });

            // Click on search result to add role
            $(document).on('click', '#vm-drive-management-modal .vm-search-result-item', (e: any) => {
                e.preventDefault();
                const $item = $(e.currentTarget);
                const roleId = $item.data('entity-id');
                const roleName = $item.data('entity-name');
                const memberCount = parseInt(String($item.data('member-count') || 0), 10) || 0;
                if (roleId && roleName) {
                    this.selectedRoles[roleId] = { id: roleId, name: roleName, access_type: 'contributor', member_count: memberCount };
                    this.renderRolesList();
                    // Clear search
                    const $results = this.$root!.find('.vm-search-results[data-context="entities"]');
                    ($results as any).hide().empty();
                    (this.$root!.find('.vm-user-search-input[data-context="entities"]') as any).val('');
                }
            });

            // Save
            $(document).on('click', '#vm-drive-management-modal #vm-drive-mgmt-save-btn', () => {
                this.save();
            });
        }

        public open(driveType: 'media_gallery' | 'user_files' | 'my_files' | 'org_drive'): void {
            this.currentDrive = driveType;
            this.ensureModal();
            const title = `${vmDriveManagement?.strings?.title || 'Drive Management'} ${this.getDriveLabel(driveType)}`;
            this.setTitle(title);
            this.show();
			// Normalize save button classes to match admin buttons
			try {
				const $btn = this.$root!.find('#vm-drive-mgmt-save-btn');
				$btn.addClass('button button-primary button-large');
			} catch {}
            this.loadRolesWithAccess();
        }

        public close(): void {
            this.isOpen = false;
            const $el = $('#vm-drive-management-modal');
            ($el as any).hide();
            ($el.find('.wc-backbone-modal-backdrop') as any).hide();
        }

        private ensureModal(): void {
            if (!this.$root) {
                this.$root = $('#vm-drive-management-modal');
            }
        }

        private show(): void {
            this.isOpen = true;
            const $el = this.$root!;
            ($el as any).css('display', 'flex');
        }

        private setTitle(text: string): void {
            this.$root!.find('span.settings_modal_title').text(text);
        }

        private getDriveLabel(drive: string): string {
            switch (drive) {
                case 'org_drive': return 'Org Drive';
                case 'user_files': return 'User Files';
                case 'my_files': return 'My Files';
                case 'media_gallery': return 'Media Library';
                default: return drive;
            }
        }

        private renderRoleItem(role: any): string {
            const memberCount = role?.member_count || 0;
            const access = this.normalizeAccessType(role?.access_type);
            const isVmAdmin = role?.id === 'vm-admin' || role?.id === 'vm_admin';
            const disabledClass = isVmAdmin ? ' vm-user-disabled' : '';
            return `
                <div class="vm-user-item${disabledClass}" data-user-id="${role.id}" data-entity-type="system_role">
                    <div class="vm-user-avatar vm-entity-system_role">
                        <span class="dashicons dashicons-admin-settings"></span>
                    </div>
                    <div class="vm-user-info">
                        <div class="vm-user-name">
                            ${role.name}
                            <span class="vm-entity-badge vm-badge-system_role">Role</span>
                        </div>
                        <div class="vm-user-email">
                            System role${memberCount ? ` • ${memberCount} members` : ''}
                        </div>
                    </div>
                    <div class="vm-user-role">
                        ${
                            isVmAdmin
                                ? `<select class="vm-role-select" disabled><option value="editor" selected>Editor</option></select>`
                                : `<select class="vm-role-select vm-drive-mgmt-access-select" data-user-id="${role.id}">
                                       <option value="viewer" ${access === 'viewer' ? 'selected' : ''}>Viewer</option>
                                       <option value="contributor" ${access === 'contributor' ? 'selected' : ''}>Contributor</option>
                                       <option value="editor" ${access === 'editor' ? 'selected' : ''}>Editor</option>
                                   </select>`
                        }
                    </div>
                    ${isVmAdmin ? `` : `<button type="button" class="vm-remove-user" data-user-id="${role.id}" data-context="entities">×</button>`}
                </div>
            `;
        }

        private async loadRolesWithAccess(): Promise<void> {
            const $list = this.$root!.find('.vm-users-list[data-context="entities"]');
            $list.html(`<div class="vm-loading-spinner"><div class="spinner"></div><p>${vmDriveManagement?.strings?.loading || 'Loading...'}</p></div>`);
            try {
                const data = await $.post(vmDriveManagement?.ajaxUrl || '/wp-admin/admin-ajax.php', {
                    action: 'get_roles_with_drive_access',
                    drive_type: this.currentDrive,
                    nonce: vmDriveManagement?.nonce || '',
                });
                if (data?.success && Array.isArray(data.data)) {
                    if (data.data.length === 0) {
                        $list.html(`<p class="vm-no-users">${vmDriveManagement?.strings?.noRolesConfigured || 'No roles configured yet.'}</p>`);
                        return;
                    }
                    // Seed selected roles and render
                    this.selectedRoles = {};
                    data.data.forEach((r: any) => {
                        this.selectedRoles[r.id] = {
                            id: r.id,
                            name: r.name,
                            access_type: this.normalizeAccessType(r.access_type),
                            member_count: r.member_count || 0
                        };
                    });
                    // Ensure VM Admin is present and fixed/grayed out
                    if (!this.selectedRoles['vm-admin']) {
                        this.selectedRoles['vm-admin'] = {
                            id: 'vm-admin',
                            name: 'VM Admin',
                            access_type: 'editor',
                            member_count: 0
                        };
                    }
                    this.renderRolesList();
                } else {
                    $list.html(`<p class="vm-no-users">${vmDriveManagement?.strings?.noRolesConfigured || 'No roles configured yet.'}</p>`);
                }
            } catch {
                $list.html(`<p class="vm-no-users">${vmDriveManagement?.strings?.noRolesConfigured || 'No roles configured yet.'}</p>`);
            }
        }

        private renderRolesList(): void {
            const $list = this.$root!.find('.vm-users-list[data-context="entities"]');
            const roles: Array<{ id: string; name: string; access_type: 'viewer' | 'contributor' | 'editor'; member_count?: number }> = [];
            for (const key in this.selectedRoles) {
                if (Object.prototype.hasOwnProperty.call(this.selectedRoles, key)) {
                    roles.push(this.selectedRoles[key]);
                }
            }
            if (!roles.length) {
                $list.html(`<p class="vm-no-users">${vmDriveManagement?.strings?.noRolesConfigured || 'No roles configured yet.'}</p>`);
                return;
            }
            const html = roles.map((r: any) => this.renderRoleItem(r)).join('');
            $list.html(html);
        }

        private async searchRoles(term: string): Promise<void> {
            const $results = this.$root!.find('.vm-search-results[data-context="entities"]');
            $results.html(`<div class="vm-search-loading">${vmDriveManagement?.strings?.searching || 'Searching...'}</div>`);
            ($results as any).show();
            try {
                const resp = await $.post(vmDriveManagement?.ajaxUrl || '/wp-admin/admin-ajax.php', {
                    action: 'list_system_roles_raw',
                    search_term: term,
                    nonce: vmDriveManagement?.nonce || '',
                });
                const entities = (resp?.success && Array.isArray(resp.data)) ? resp.data.filter((e: any) => e.type === 'system_role') : [];
                if (!entities.length) {
                    $results.html(`<div class="vm-search-no-results">${vmDriveManagement?.strings?.noResults || 'No results found'}</div>`);
                    return;
                }
                const html = entities.map((e: any) => `
                    <div class="vm-search-result-item vm-entity-system_role"
                        data-entity-id="${e.id}"
                        data-entity-name="${e.name}"
                        data-entity-type="system_role"
                        data-member-count="${e.member_count || 0}"
                        data-context="entities">
                        <div class="vm-user-avatar vm-entity-system_role">
                            <span class="dashicons dashicons-admin-settings"></span>
                        </div>
                        <div class="vm-user-details">
                            <div class="vm-user-name">
                                ${e.name}
                                <span class="vm-entity-badge vm-badge-system_role">Role</span>
                            </div>
                            <div class="vm-user-email">System role${e.member_count ? ` • ${e.member_count} members` : ''}</div>
                        </div>
                    </div>
                `).join('');
                $results.html(html);
            } catch {
                $results.html(`<div class="vm-search-no-results">${vmDriveManagement?.strings?.noResults || 'No results found'}</div>`);
            }
        }

        private async save(): Promise<void> {
            $('.vm-drive-management-error').hide();
            const rolesPayload: Array<{ id: string; access_type: 'viewer' | 'contributor' | 'editor' }> = [];
            for (const key in this.selectedRoles) {
                if (Object.prototype.hasOwnProperty.call(this.selectedRoles, key)) {
                    const r = this.selectedRoles[key];
                    rolesPayload.push({ id: r.id, access_type: r.access_type });
                }
            }
			const $btn = $('#vm-drive-management-modal #vm-drive-mgmt-save-btn');
			const btnEl = $btn.get(0);
			if (this.spinnerController && btnEl) {
				this.spinnerController.toggleSpinnerOnButton(btnEl, true);
                $btn.addClass('disabled');
			}
            try {
                const resp = await $.post(vmDriveManagement?.ajaxUrl || '/wp-admin/admin-ajax.php', {
                    action: 'save_drive_management_roles',
                    drive_type: this.currentDrive,
                    roles: rolesPayload,
                    nonce: vmDriveManagement?.nonce || ''
                });
                if (resp?.success) {
                    this.close();
                } else {
                    $('.vm-drive-management-error').text(resp?.data?.message || 'Failed to save').show();
                }
            } catch (_e) {
                $('.vm-drive-management-error').text('Network error saving changes.').show();
			} finally {
				if (this.spinnerController && btnEl) {
					this.spinnerController.toggleSpinnerOnButton(btnEl, false);
                    $btn.removeClass('disabled');
				}
            }
        }

        private normalizeAccessType(value: any): 'viewer' | 'contributor' | 'editor' {
            // New format
            if (value === 'viewer' || value === 'contributor' || value === 'editor') {
                return value;
            }
            // Old format (backward compat)
            if (value === 'manage_access') return 'editor';
            if (value === 'edit') return 'contributor';
            return 'contributor';
        }
    }

    $(document).ready(() => {
        // Expose globally
        (window as any).DriveManagementModal = DriveManagementModal;
        (window as any).driveManagementModal = new DriveManagementModal();
    });
})((window as any).jQuery);


